import { useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useUploadDmarcReport } from "@/hooks/use-email-auth";

interface ReportUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReportUploadDialog({ open, onOpenChange }: ReportUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const mutation = useUploadDmarcReport();

  async function handleUpload() {
    if (!file) return;

    const buffer = await file.arrayBuffer();
    const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));

    await mutation.mutateAsync({ file_data: base64, filename: file.name });
    setFile(null);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload DMARC Report</DialogTitle>
          <DialogDescription>
            Upload a DMARC aggregate report (XML or ZIP). These are typically
            sent to your DMARC RUA address by email providers.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label
            htmlFor="dmarc-report-file"
            className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 cursor-pointer hover:bg-accent/50 transition-colors"
          >
            <Upload className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {file ? file.name : "Click to select XML or ZIP file"}
            </p>
            <input
              id="dmarc-report-file"
              type="file"
              accept=".xml,.zip,.gz"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!file || mutation.isPending}>
              {mutation.isPending ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
